/**
 * User Preferences Manager
 * Handles video playback preferences including autoplay and mute settings
 */

const PREFERENCES_KEY = 'userVideoPreferences';

// Default preferences
const DEFAULT_PREFERENCES = {
    enableAutoplay: false,
    muteVideosByDefault: false,
    hideOwnLikeCount: false,
};


export const getUserPreferences = () => {
    try {
        const stored = localStorage.getItem(PREFERENCES_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            return { ...DEFAULT_PREFERENCES, ...parsed };
        }
    } catch (error) {
        console.error('Error reading user preferences:', error);
    }
    return DEFAULT_PREFERENCES;
};

export const saveUserPreferences = (preferences) => {
    try {
        const currentPrefs = getUserPreferences();
        const updatedPrefs = { ...currentPrefs, ...preferences };
        localStorage.setItem(PREFERENCES_KEY, JSON.stringify(updatedPrefs));

        // Dispatch custom event to notify components of preference changes
        window.dispatchEvent(new CustomEvent('preferencesChanged', {
            detail: updatedPrefs
        }));

        return updatedPrefs;
    } catch (error) {
        console.error('Error saving user preferences:', error);
        return null;
    }
};


export const updatePreference = (key, value) => {
    const currentPrefs = getUserPreferences();
    return saveUserPreferences({ ...currentPrefs, [key]: value });
};


export const isAutoplayEnabled = () => {
    const prefs = getUserPreferences();
    return prefs.enableAutoplay;
};


export const shouldMuteByDefault = () => {
    const prefs = getUserPreferences();
    return prefs.muteVideosByDefault;
};


export const shouldHideOwnLikeCount = () => {
    const prefs = getUserPreferences();
    return !!prefs.hideOwnLikeCount;
};

/**
 * Determines whether a like count should be hidden for the given piece of
 * content. Only ever hides the count for the currently logged-in user's own
 * content, and only when they've enabled the "hide my like count" preference.
 */
export const isOwnLikeCountHidden = (authorId, currentUserId) => {
    if (!authorId || !currentUserId) {
        return false;
    }
    return String(authorId) === String(currentUserId) && shouldHideOwnLikeCount();
};

/**
 * Reset preferences to defaults
 */
export const resetPreferences = () => {
    return saveUserPreferences(DEFAULT_PREFERENCES);
};

/**
 * Logout/session-clear flows wipe localStorage wholesale (auth tokens,
 * redux-persist state, etc). Preferences like "hide my like count" are a
 * device-level setting, not an auth artifact, so they should survive a
 * logout -> login cycle. Call this instead of `localStorage.clear()` in
 * those flows to clear everything else while keeping preferences intact.
 */
export const clearStorageKeepingPreferences = () => {
    const preserved = localStorage.getItem(PREFERENCES_KEY);
    localStorage.clear();
    if (preserved) {
        localStorage.setItem(PREFERENCES_KEY, preserved);
    }
};
